<!-- Notifications toggle -->
<button mat-icon-button (click)="openPanel()" #notificationsOrigin>
    @if (unreadCount > 0) {
        <span
            class="absolute left-0 right-0 top-0 flex h-3 items-center justify-center"
        >
            <span
                class="ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 px-1 text-xs font-medium text-indigo-50"
            >
                {{ unreadCount }}
            </span>
        </span>
    }
    <mat-icon [svgIcon]="'heroicons_outline:bell'"></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary"
        >
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:x-mark'"
                    ></mat-icon>
                </button>
            </div>
            <div class="text-lg font-medium leading-10">Notifications</div>
            <div class="ml-auto">
                <button
                    class="dark:text-white"
                    mat-icon-button
                    [matTooltip]="'Mark all as read'"
                    [disabled]="unreadCount === 0"
                    (click)="markAllAsRead()"
                >
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:envelope-open'"
                    ></mat-icon>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div
            class="bg-card relative flex flex-auto flex-col overflow-y-auto sm:max-h-120"
        >
            <!-- Tabs -->
            <mat-tab-group 
                [selectedIndex]="activeTab === 'notifications' ? 0 : 1" 
                (selectedIndexChange)="activeTab = $event === 0 ? 'notifications' : 'timeline'">
                <mat-tab label="Notifications">
                    <div class="divide-y">
                        <!-- Notifications -->
                        @for (
                            notification of notifications;
                            track trackByFn($index, notification)
                        ) {
                <div
                    class="group flex hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                    [ngClass]="{ unread: !notification.read }"
                >
                    <!-- Notification with a link -->
                    @if (notification.link) {
                        <!-- Normal links -->
                        @if (!notification.useRouter) {
                            <a
                                class="flex flex-auto cursor-pointer py-5 pl-6"
                                [href]="notification.link"
                            >
                                <ng-container
                                    *ngTemplateOutlet="notificationContent"
                                ></ng-container>
                            </a>
                        }
                        <!-- Router links -->
                        @if (notification.useRouter) {
                            <a
                                class="flex flex-auto cursor-pointer py-5 pl-6"
                                [routerLink]="notification.link"
                            >
                                <ng-container
                                    *ngTemplateOutlet="notificationContent"
                                ></ng-container>
                            </a>
                        }
                    }

                    <!-- Notification without a link -->
                    @if (!notification.link) {
                        <div class="flex flex-auto py-5 pl-6">
                            <ng-container
                                *ngTemplateOutlet="notificationContent"
                            ></ng-container>
                        </div>
                    }

                    <!-- Actions -->
                    <div class="relative my-5 ml-2 mr-6 flex flex-col">
                        <!-- Indicator -->
                        <button
                            class="h-6 min-h-6 w-6"
                            mat-icon-button
                            (click)="toggleRead(notification)"
                            [matTooltip]="
                                notification.read
                                    ? 'Mark as unread'
                                    : 'Mark as read'
                            "
                        >
                            <span
                                class="h-2 w-2 rounded-full"
                                [ngClass]="{
                                    'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                                        notification.read,
                                    'bg-primary': !notification.read,
                                }"
                            ></span>
                        </button>
                        <!-- Remove -->
                        <button
                            class="h-6 min-h-6 w-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="delete(notification)"
                            [matTooltip]="'Remove'"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:x-mark'"
                            ></mat-icon>
                        </button>
                    </div>
                </div>

                <!-- Notification content template -->
                <ng-template #notificationContent>
                    <!-- Icon -->
                    @if (notification.icon && !notification.image) {
                        <div
                            class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700"
                        >
                            <mat-icon
                                class="icon-size-5"
                                [svgIcon]="notification.icon"
                            >
                            </mat-icon>
                        </div>
                    }
                    <!-- Image -->
                    @if (notification.image) {
                        <img
                            class="mr-4 h-8 w-8 shrink-0 overflow-hidden rounded-full object-cover object-center"
                            [src]="notification.image"
                            [alt]="'Notification image'"
                        />
                    }
                    <!-- Title, description & time -->
                    <div class="flex flex-auto flex-col">
                        @if (notification.title) {
                            <div
                                class="line-clamp-1 font-semibold"
                                [innerHTML]="notification.title"
                            ></div>
                        }
                        @if (notification.description) {
                            <div
                                class="line-clamp-2"
                                [innerHTML]="notification.description"
                            ></div>
                        }
                        <div class="text-secondary mt-2 text-sm leading-none">
                            {{ notification.time | date: 'MMM dd, h:mm a' }}
                        </div>
                    </div>
                </ng-template>
                        }

                        <!-- No notifications -->
                        @if (!notifications || !notifications.length) {
                <div
                    class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start"
                >
                    <div
                        class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
                    >
                        <mat-icon
                            class="text-primary-700 dark:text-primary-50"
                            [svgIcon]="'heroicons_outline:bell'"
                        ></mat-icon>
                    </div>
                    <div class="mt-5 text-2xl font-semibold tracking-tight">
                        No notifications
                    </div>
                    <div
                        class="text-secondary mt-1 w-full max-w-60 text-center text-md"
                    >
                        When you have notifications, they will appear here.
                    </div>
                </div>
                        }
                    </div>
                </mat-tab>
                
                <mat-tab label="Activity Timeline">
                    <div class="p-4">
                        <!-- Timeline Search -->
                        <div class="mb-4 flex items-center">
                            <div class="flex-1">
                                <div class="relative">
                                    <input
                                        [(ngModel)]="searchQuery"
                                        (keyup.enter)="handleTimelineSearch()"
                                        class="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-primary focus:outline-none dark:border-gray-600 dark:bg-gray-700"
                                        placeholder="Search timeline..."
                                        type="text"
                                    />
                                    <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <mat-icon
                                            class="text-gray-400 dark:text-gray-500 icon-size-5"
                                            [svgIcon]="'heroicons_outline:magnifying-glass'"
                                        ></mat-icon>
                                    </div>
                                </div>
                            </div>
                            <button
                                mat-icon-button
                                class="ml-2"
                                (click)="clearTimelineSearch()"
                                [matTooltip]="'Clear search'"
                            >
                                <mat-icon
                                    class="icon-size-5"
                                    [svgIcon]="'heroicons_outline:arrow-path'"
                                ></mat-icon>
                            </button>
                        </div>
                        
                        <!-- Timeline Activities -->
                        <div class="divide-y">
                            @for (
                                activity of timelineActivities;
                                track trackByFn($index, activity)
                            ) {
                                <div class="py-4 flex items-start">
                                    <!-- Activity Circle -->
                                    <div 
                                        class="w-3 h-3 mt-1.5 mr-4 rounded-full flex-shrink-0"
                                        [ngClass]="getTimelineCircleClass(activity.action)"
                                    ></div>
                                    
                                    <!-- Activity Content -->
                                    <div class="flex-1">
                                        <div class="font-medium">
                                            {{ getOrderStatus(activity.action, activity.title) }}
                                        </div>
                                        <div class="text-sm text-gray-600 dark:text-gray-400">
                                            {{ activity.description }}
                                        </div>

                                        @if (activity.sample_photos?.length) {
                                            <div class="mt-2 flex flex-wrap gap-2">
                                                @for (photo of activity.sample_photos; track photo.url) {
                                                    <button
                                                        type="button"
                                                        class="h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                                                        (click)="openDeliveryPhoto(photo.url)"
                                                    >
                                                        <img
                                                            class="h-full w-full object-cover"
                                                            [src]="photo.thumbnail_url || photo.url"
                                                            [alt]="photo.original_name || 'Production sample photo'"
                                                        />
                                                    </button>
                                                }
                                            </div>
                                        }
                                        
                                        <!-- Delivery Photo and Text (only for order_delivered) -->
                                        @if (activity.action === 'order_delivered') {
                                            @if (activity.delivery_photo_url) {
                                                <div class="mt-2">
                                                    <img 
                                                        [src]="activity.delivery_photo_url" 
                                                        alt="Delivery Photo"
                                                        class="w-full max-w-48 h-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow"
                                                        (click)="openDeliveryPhoto(activity.delivery_photo_url)"
                                                    />
                                                </div>
                                            }
                                            @if (activity.delivery_text) {
                                                <div class="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-md border-l-4 border-green-500">
                                                    <p class="text-sm text-green-700 dark:text-green-400 font-medium">
                                                        {{ activity.delivery_text }}
                                                    </p>
                                                </div>
                                            }
                                        }
                                        
                                        <div class="text-xs text-gray-500 mt-1">
                                            {{ activity.updated_at | date: 'EEE, MMM d, y' }}
                                        </div>
                                    </div>
                                </div>
                            }
                            
                            <!-- No activities -->
                            @if (!timelineActivities || !timelineActivities.length) {
                                <div class="flex flex-col items-center justify-center py-12">
                                    <div
                                        class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
                                    >
                                        <mat-icon
                                            class="text-primary-700 dark:text-primary-50"
                                            [svgIcon]="'heroicons_outline:clock'"
                                        ></mat-icon>
                                    </div>
                                    <div class="mt-5 text-2xl font-semibold tracking-tight">
                                        No activities
                                    </div>
                                    <div class="text-secondary mt-1 w-full max-w-60 text-center text-md">
                                        Activity timeline is empty or try a different search.
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </mat-tab>
            </mat-tab-group>
        </div>
    </div>
</ng-template>
